import React, { useState, useEffect } from "react";
import { ref, onValue } from "firebase/database";
import { db } from "./firebase/config";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { createSlug } from "./utils/slugUtils";

const NewsScreen = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const newsRef = ref(db, "news");
    onValue(newsRef, (snapshot) => {
      if (snapshot.exists()) {
        const newsData = snapshot.val();
        const newsArray = Object.entries(newsData).map(([id, data]) => {
          // Log để debug thời gian

          return {
            id,
            ...data,
            // Nếu không có createdAt, đặt thời gian là 0 để bài viết xuống cuối
            createdAt: data.createdAt ? new Date(data.createdAt).getTime() : 0,
            // Thêm slug từ tiêu đề
            slug: createSlug(data.title),
          };
        });

        // Log để debug việc sắp xếp
        console.log(
          "Before sorting:",
          newsArray.map((a) => ({
            title: a.title,
            createdAt: new Date(a.createdAt).toISOString(),
          }))
        );

        const sortedArticles = newsArray.sort(
          (a, b) => b.createdAt - a.createdAt
        );

        // Log kết quả sắp xếp
        console.log(
          "After sorting:",
          sortedArticles.map((a) => ({
            title: a.title,
            createdAt: new Date(a.createdAt).toISOString(),
          }))
        );

        setArticles(sortedArticles);
      }
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    document.title = "Tin Tức & Cập Nhật | AutoWork";
  }, []);

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="animate-pulse">
          <div className="h-64 bg-gradient-to-r from-blue-500 to-purple-600"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-8"></div>
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <div
                  key={n}
                  className="bg-white rounded-lg shadow-md overflow-hidden"
                >
                  <div className="h-48 bg-gray-200"></div>
                  <div className="p-6">
                    <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded"></div>
                      <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Tin Tức & Cập Nhật | AutoWork</title>
        <meta
          name="description"
          content="Cập nhật tin tức mới nhất về công nghệ tự động hóa marketing, quản lý fanpage và các giải pháp marketing online hiệu quả."
        />
        <meta
          name="keywords"
          content="tin tức marketing, tự động hóa, quản lý fanpage, content marketing, autowork"
        />
        <meta property="og:title" content="Tin Tức & Cập Nhật | AutoWork" />
        <meta
          property="og:description"
          content="Cập nhật tin tức mới nhất về công nghệ tự động hóa marketing, quản lý fanpage và các giải pháp marketing online hiệu quả."
        />
        <link rel="canonical" href="https://autowork.vn/news" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "Tin Tức & Cập Nhật | AutoWork",
            description:
              "Cập nhật tin tức mới nhất về công nghệ tự động hóa marketing, quản lý fanpage và các giải pháp marketing online hiệu quả.",
            url: "https://autowork.vn/news",
            blogPost: articles.map((article) => ({
              "@type": "BlogPosting",
              headline: article.title,
              description: article.description,
              image: article.imageUrl,
              datePublished: new Date(article.createdAt).toISOString(),
              dateModified: new Date(article.createdAt).toISOString(),
              author: {
                "@type": "Organization",
                name: "AutoWork",
              },
            })),
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-0 md:pt-20">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-blue-600 via-blue-500 to-purple-600 text-white">
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <div className="text-center">
              <h1 className="text-3xl md:text-4xl font-bold mb-3">
                Tin Tức & Cập Nhật
              </h1>
              <p className="text-lg text-gray-100 max-w-2xl mx-auto">
                Cập nhật những tin tức mới nhất về trí tuệ nhân tạo (AI), công
                nghệ hiện đại và các giải pháp tự động hóa đột phá
              </p>
            </div>
          </div>
        </div>

        {/* News Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {articles.map((article) => (
              <article
                key={article.id}
                className="group bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                <div className="relative overflow-hidden">
                  {article.imageUrl ? (
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-48 object-cover transform group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gradient-to-br from-gray-100 to-gray-200"></div>
                  )}
                </div>
                <div className="p-6">
                  <div className="text-sm text-gray-500 mb-3 flex items-center">
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    {formatDate(article.createdAt)}
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300">
                    <Link to={`/news/${article.slug}`}>{article.title}</Link>
                  </h2>
                  <p className="text-gray-600 line-clamp-3 mb-4">
                    {article.description}
                  </p>
                  <Link
                    to={`/news/${article.slug}`}
                    className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Đọc thêm
                    <svg
                      className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </Link>
                </div>
              </article>
            ))}
          </div>

          {articles.length === 0 && (
            <div className="text-center py-16">
              <div className="text-gray-400 mb-4">
                <svg
                  className="w-16 h-16 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5a2.5 2.5 0 00-2.5-2.5H15"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">
                Chưa có bài viết nào
              </h3>
              <p className="text-gray-500">
                Vui lòng quay lại sau để xem các bài viết mới.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NewsScreen;
